import { useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { useListPosts } from "@workspace/api-client";
import { useQuery } from "@tanstack/react-query";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

function PostCard({ post, index }: { post: any; index: number }) {
  const { data: media } = useQuery<any[]>({
    queryKey: ["media", "POST", post.id],
    queryFn: async () => {
      const res = await fetch(`/api/media/POST/${post.id}`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
    enabled: !!post.id,
  });

  const coverUrl =
    post.coverImage ||
    media?.find((m: any) => m.isFeatured)?.url ||
    media?.[0]?.url;

  return (
    <Link href={`/blog/${post.slug}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className="group cursor-pointer h-full flex flex-col"
      >
        <div className="aspect-[16/9] w-full rounded-xl bg-muted/30 mb-6 overflow-hidden relative">
          {coverUrl ? (
            <img src={coverUrl} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20 group-hover:scale-105 transition-transform duration-500"></div>
          )}
          <div className="absolute top-4 left-4">
            <span className="rounded-full bg-background/80 backdrop-blur-md px-3 py-1 text-xs font-medium text-foreground">
              {post.category}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
          <span>{post.createdAt ? format(new Date(post.createdAt), 'MMM d, yyyy') : 'Recent'}</span>
          <span>·</span>
          <span>{post.readTime || 5} min read</span>
        </div>
        <h3 className="font-display text-xl font-bold mb-2 group-hover:text-primary transition-colors">{post.title}</h3>
        <p className="text-muted-foreground text-sm flex-1">{post.excerpt}</p>
      </motion.div>
    </Link>
  );
}

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const { data: postsData, isLoading } = useListPosts();

  const posts = postsData?.posts && postsData.posts.length > 0 ? postsData.posts : [];
  const categories = ["All", "Web Development", "Mobile Apps", "AI & Automation", "Business Growth", "Tech Tips"];

  const filteredPosts = activeCategory === "All" 
    ? posts 
    : posts.filter((p: any) => p.category === activeCategory);

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO
        title="Engineering Blog | Insights & Ideas"
        description="Technical deep-dives, industry trends, and engineering guides from our elite team of software engineers."
      />
      <JsonLd schema={[
        {
          "@context": "https://schema.org",
          "@type": "Blog",
          "@id": "https://acceleratebyte.com/blog",
          "name": "AccelerateByte Engineering Blog",
          "url": "https://acceleratebyte.com/blog",
          "description": "Technical deep-dives, industry trends, and engineering guides from our elite team of software engineers.",
          "publisher": { "@id": "https://acceleratebyte.com/#organization" }
        },
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "AccelerateByte Blog Posts",
          "url": "https://acceleratebyte.com/blog",
          "itemListElement": posts.map((post: any, i: number) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Article",
              "headline": post.title,
              "url": `https://acceleratebyte.com/blog/${post.slug}`,
              "description": post.excerpt,
              "datePublished": post.createdAt,
              "publisher": { "@id": "https://acceleratebyte.com/#organization" }
            }
          }))
        }
      ]} />
      <section className="pt-32 pb-16 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 tracking-tight">Insights & Ideas</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Technical deep-dives, industry trends, and engineering guides from our elite team.
          </p>
        </div>
      </section>

      <section className="py-8 px-4 container mx-auto">
        <div className="flex flex-wrap justify-center gap-2 mb-16">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat 
                  ? "bg-primary text-primary-foreground shadow-sm" 
                  : "bg-muted/50 text-muted-foreground hover:bg-muted"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {filteredPosts.map((post: any, i: number) => (
            <PostCard key={post.slug} post={post} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
